import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { HeaderButton, BaseRecordView } from '../common/sections'
import { SignatureView } from './SignatureView'
import { SignatureModal } from '../settings/SignatureModal'

const viewWillAppear = async ({ urlParams, owner }: PocketComponent) => {
    if (!urlParams.id) {
        throw new RecordNotFoundError('Signature not found')
    }

    const result = await RecordFactory.getInstance().fetchSignature(urlParams.id)
    if (!result.ok) {
        throw new RecordNotFoundError('Signature not found')
    }

    const signature = await RecordFactory.getInstance().loadSignature(urlParams.id)
    if (!signature) {
        throw new RecordNotFoundError('Signature not found')
    }

    const contract = signature.contract
    if (!contract) {
        throw new RecordNotFoundError('Contract not found')
    }

    const user = owner.id === signature.owner?.id ? signature.person : signature.owner
    if (!user) {
        throw new RecordNotFoundError('User not found')
    }

    return { signature, contract, user }
}

const render = ({
    data,
    base: { owner, logAndDisplayError },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { signature, contract, user } = data

    const navigate = useNavigate()

    usePageTitle(contract.name ?? '')

    const { findLastHistoryEntry } = useHistoryTrackerContext()

    const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false)

    const forVendor = owner.id === signature.owner?.id

    const [confirmationModalData, setConfirmationModalData] = useState<{
        title: string
        confirmationButtonText: string
        body: string
        onConfirm: () => void
        secondaryButton?: {
            title: string
            onClick: () => void
        }
    } | null>(null)

    const handleSignatureDone = async (data: string) => {
        setIsSignatureModalOpen(false)
        const result = await RecordFactory.getInstance().signContract(
            signature.clone({ signature_data: data })
        )
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        window.location.reload()
    }

    const handleCancel = async (shouldNotify: boolean) => {
        const result = await RecordFactory.getInstance().deleteSignature(
            signature.id!,
            !shouldNotify
        )
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        if (shouldNotify) {
            navigate(`/messages/${forVendor ? signature.person?.id : signature.owner?.id}`)
        }
        else {
            window.location.reload()
        }
    }

    const handleResend = async () => {
        const result = await RecordFactory.getInstance().sendSignature(signature, true)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        navigate(`/messages/${forVendor ? signature.person?.id : signature.owner?.id}`)
    }

    const headerButtons = (() => {
        if (signature.signedDate || signature.cancelledDate) {
            return []
        }

        const buttons: HeaderButton[] = []

        if (!forVendor || signature.person === user?.id) {
            buttons.push({ title: 'Sign', onClick: () => setIsSignatureModalOpen(true) })
        }
        else if (forVendor) {
            buttons.push({
                title: 'Cancel',
                onClick: () =>
                    setConfirmationModalData({
                        title: 'Cancel contract?',
                        confirmationButtonText: 'Cancel & notify',
                        body: `This action cannot be undone. Select "Cancel & notify" to notify the client of the cancellation, otherwise select "Cancel contract".`,
                        onConfirm: () => handleCancel(true),
                        secondaryButton: {
                            title: 'Cancel contract',
                            onClick: () => handleCancel(false),
                        },
                    }),
            })
        }

        if (forVendor) {
            buttons.push({
                title: 'Resend',
                onClick: () =>
                    setConfirmationModalData({
                        title: 'Resend contract',
                        confirmationButtonText: 'Resend',
                        body: 'Would you like to resend this contract to this client?',
                        onConfirm: handleResend,
                    }),
            })
        }

        return buttons
    })()

    const backHistoryEntry = findLastHistoryEntry({
        excluding: /^\/settings\/contracts.*\/(new|edit|copy)/,
    })

    return (
        <BaseRecordView
            title="Contract"
            backButton={
                backHistoryEntry !== undefined
                    ? {
                          title: backHistoryEntry.title ?? 'Back',
                          onClick: backHistoryEntry.goBackTo,
                      }
                    : {
                          title: 'Contracts',
                          onClick: () => navigate('/dashboards/contracts'),
                      }
            }
            headerButtons={headerButtons}
            tag={signature.statusTag ?? undefined}
        >
            <SignatureView signature={signature} contract={contract} user={user} />

            <SignatureModal
                isOpen={isSignatureModalOpen}
                onClose={() => setIsSignatureModalOpen(false)}
                onDone={handleSignatureDone}
            />

            {confirmationModalData !== null && (
                <Modal
                    isOpen
                    onClose={() => setConfirmationModalData(null)}
                    size="small"
                    title={confirmationModalData.title}
                    showDividers={false}
                    footer={
                        <div className="flex w-full items-center justify-end gap-x-4">
                            {confirmationModalData.secondaryButton && (
                                <Button
                                    intent="tertiary_deprecated"
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation()
                                        confirmationModalData.secondaryButton?.onClick()
                                    }}
                                >
                                    {confirmationModalData.secondaryButton.title}
                                </Button>
                            )}
                            <Button
                                intent="primary"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation()
                                    confirmationModalData.onConfirm()
                                }}
                            >
                                {confirmationModalData.confirmationButtonText}
                            </Button>
                        </div>
                    }
                >
                    {confirmationModalData.body}
                </Modal>
            )}
        </BaseRecordView>
    )
}

export const SignaturePage = usePSComponent(render, viewWillAppear)
